import { getCreateReferenceId } from '@quromedical/fhir-common';
import {
  type AdmissionGetResponse,
  AdmissionGetResponseSchema,
  DisplayReferenceSchema,
  isValidReference,
  type VitalsLiveDataMeasures,
} from '@quromedical/models';
import { NEWS2ClinicalRisk, type QuroScoreResult } from '@quromedical/news';
import { getPatientNEWS2Score, requiresUrgentResponse } from 'core/vitals';
import { useActiveAssignedAdmissions } from 'hooks/useActiveAssignedAdmissions';
import { useLiveData } from 'integration/live-vitals';
import type { z } from 'zod';

const AdmissionWithPatientSchema = AdmissionGetResponseSchema.extend({
  patient: DisplayReferenceSchema,
});
export type AdmissionWithPatient = z.infer<typeof AdmissionWithPatientSchema>;

export interface AdmissionRisk {
  admission: AdmissionWithPatient;
  news?: QuroScoreResult;
  vitals?: VitalsLiveDataMeasures;
}

const requiresResponse = (
  admissionRisk: AdmissionRisk,
): admissionRisk is Required<AdmissionRisk> => {
  const { news } = admissionRisk;

  if (!news) {
    return false;
  }

  return requiresUrgentResponse(news.risk);
};

const filterAlerts =
  (highOnly: boolean) =>
  (admissionRisk: AdmissionRisk): admissionRisk is Required<AdmissionRisk> => {
    const response = requiresResponse(admissionRisk);

    if (!response) {
      return false;
    }

    if (!highOnly) {
      return true;
    }

    return admissionRisk.news.risk === NEWS2ClinicalRisk.High;
  };

const hasPatient = (
  admission: AdmissionGetResponse,
): admission is AdmissionWithPatient => !!admission.patient?.reference;

export const useUrgentReviewList = (highOnly: boolean) => {
  const { vitals: liveData = {}, updated } = useLiveData();
  const { data: admissions = [], isLoading: loadingAdmissions } =
    useActiveAssignedAdmissions({});

  const admissionsWithPatients = admissions.filter(
    hasPatient,
  ) as AdmissionWithPatient[];
  const admissionRisks = admissionsWithPatients
    .map<AdmissionRisk>((admission) => {
      const patientId = isValidReference(admission.patient)
        ? getCreateReferenceId(admission.patient)
        : '';
      const patientVitals = liveData[patientId];

      return {
        admission,
        news: getPatientNEWS2Score(patientVitals, admission.oxygen),
        vitals: patientVitals,
      };
    })
    .filter(filterAlerts(highOnly));

  return { admissionRisks, updated, loadingAdmissions };
};
